import { RootState } from '../../../app/store';
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"
import { OrderDirection } from '../../Datatable/OrderDirection';
import ContactNameModel from '../../ContactSelector/models/ContactNameModel';
import CargoQueryModel from '../models/CargoQueryModel';
import CargoModel from '../models/CargoModel';
import { QueryItem } from '../../Tonnage/models/TonnageQueryModel';
import { SearchType } from '../../Tonnage/reducers/TonnageSearchReducer';
import { MatchingType } from '../../MatchingPreview/MatchingConstants';
import TriggerMatchingAreaModel from '../../MatchingPreview/models/TriggerMatchingAreaModel';

interface cargoSearchState {
    query: CargoQueryModel,
    searchResults: CargoModel[],
    contactSearchKeyword: string,
    contactAccountNames: ContactNameModel[],
    triggerSearch: number,
    datatableSelectedIndex: number,
    searchResultTotalRecord: number,
    checkedCargos: CargoModel[],
    searchType: SearchType,
    triggerFocusCharterer: number,
    triggerMatchingAreaModel: TriggerMatchingAreaModel<CargoModel>
}

const initialState: cargoSearchState = {
    query: {
        charterers: [],
        chartererForInput: "",
        loadPorts: [],
        loadPortAreas: [],
        countries: [],
        disports: [],
        disportAreas: [],
        dwtMin: 0,
        dwtMax: 0,
        cargoTypes: [],
        cargoGroups: [],
        dateFrom: "",
        dateTo: "",
        withinDays: null,
        orderDetail: "",
        orderBy: "updatedDate",
        orderDirection: OrderDirection.Desc,
        fixed: false,
        includeOutdatedCargoes: false
    } as CargoQueryModel,
    searchResults: [],
    contactSearchKeyword: "",
    contactAccountNames: [],
    triggerSearch: 0,
    datatableSelectedIndex: -1,
    searchResultTotalRecord: 0,
    checkedCargos: [],
    searchType: SearchType.Search,
    triggerFocusCharterer: 0,
    triggerMatchingAreaModel: {
        triggerMatchingArea: 0,
        matchingType: MatchingType.MatchingArea1,
        model: {

        } as CargoModel
    } as TriggerMatchingAreaModel<CargoModel>
}

export const CargoSearchSlice = createSlice({
    name: "CargoSearch",
    initialState,
    reducers: {
        setCharterers: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.charterers = action.payload;
        },
        setCharterersForInput: (state, action: PayloadAction<string>) => {
            state.query.chartererForInput = action.payload;
        },
        setCountries: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.countries = action.payload;
        },
        setLoadPorts: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.loadPorts = action.payload;
        },
        setLoadPortAreas: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.loadPortAreas = action.payload;
        },
        setDisports: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.disports = action.payload;
        },
        setDisportAreas: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.disportAreas = action.payload;
        },
        setDwtMin: (state, action: PayloadAction<number>) => {
            state.query.dwtMin = action.payload;
        },
        setDwtMax: (state, action: PayloadAction<number>) => {
            state.query.dwtMax = action.payload;
        },
        setCargoTypes: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.cargoTypes = action.payload;
        },
        setCargoGroups: (state, action: PayloadAction<QueryItem[]>) => {
            state.query.cargoGroups = action.payload;
        },
        setDateFrom: (state, action: PayloadAction<string>) => {
            state.query.dateFrom = action.payload;
        },
        setDateTo: (state, action: PayloadAction<string>) => {
            state.query.dateTo = action.payload;
        },
        setWithinDays: (state, action: PayloadAction<number>) => {
            state.query.withinDays = action.payload;
        },
        setOrderDetail: (state, action: PayloadAction<string>) => {
            state.query.orderDetail = action.payload;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.query.orderBy = action.payload;
        },
        setOrderDirection: (state, action: PayloadAction<OrderDirection>) => {
            state.query.orderDirection = action.payload;
        },
        setCargoSearchResults: (state, action: PayloadAction<CargoModel[]>) => {
            state.searchResults = action.payload;
        },
        setCargoContactSearchKeyword: (state, action: PayloadAction<string>) => {
            state.contactSearchKeyword = action.payload;
        },
        setTriggerCargoSearch: (state, action: PayloadAction<number>) => {
            state.triggerSearch = action.payload;
        },
        setDatatableSelectedCargoIndex: (state, action: PayloadAction<number>) => {
            state.datatableSelectedIndex = action.payload;
        },
        setSearchResultTotalRecord: (state, action: PayloadAction<number>) => {
            state.searchResultTotalRecord = action.payload;
        },
        setCargoquery: (state, action: PayloadAction<CargoQueryModel>) => {
            state.query = action.payload;
        },
        resetCargoSearch: (state) => {
            state.query = initialState.query;
        },
        setContactAccountNames: (state, action: PayloadAction<ContactNameModel[]>) => {
            state.contactAccountNames = action.payload;
        },
        setCheckedCargos: (state, action: PayloadAction<CargoModel[]>) => {
            state.checkedCargos = action.payload;
        },
        setSearchType: (state, action: PayloadAction<SearchType>) => {
            state.searchType = action.payload;
        },
        setTriggerFocusCharterer: (state, action: PayloadAction<number>) => {
            state.triggerFocusCharterer = action.payload;
        },
        setSearchFixed: (state, action: PayloadAction<boolean>) => {
            state.query.fixed = action.payload;
        },
        setIncludeOutdatedCargoes: (state, action: PayloadAction<boolean>) => {
            state.query.includeOutdatedCargoes = action.payload;
        },
        setTriggerMatchingAreaModel: (state, action: PayloadAction<TriggerMatchingAreaModel<CargoModel>>) => {
            state.triggerMatchingAreaModel = action.payload;
        },
    }
});

export const cargoSearchSelector = createSelector(
    (state: RootState) => state.cargoSearch,
    (data) => data
);

export const {
    setCharterers,
    setCharterersForInput,
    setLoadPorts,
    setCountries,
    setLoadPortAreas,
    setDisports,
    setDisportAreas,
    setDwtMin,
    setDwtMax,
    setDateFrom,
    setDateTo,
    setCargoTypes,
    setOrderDetail,
    setCargoGroups,
    setWithinDays,
    setOrderBy,
    setOrderDirection,
    setCargoSearchResults,
    setCargoContactSearchKeyword,
    setTriggerCargoSearch,
    setDatatableSelectedCargoIndex,
    setSearchResultTotalRecord,
    setCargoquery,
    resetCargoSearch,
    setContactAccountNames,
    setCheckedCargos,
    setSearchType,
    setTriggerFocusCharterer,
    setSearchFixed,
    setIncludeOutdatedCargoes,
    setTriggerMatchingAreaModel
} = CargoSearchSlice.actions;

export default CargoSearchSlice.reducer;